import React, { useRef, useState, useCallback } from "react"
import {
	audioPlayerClassName,
	spinnerClassName,
	overlayClassName,
	progressBarContainerClassName,
	progressBarFillClassName,
	timeDisplayClassName,
} from "./audioPlayer.css"
import { Box } from "./Box"
import { Button } from "./Button"
import { Inline } from "./Inline"
import Icons from "./Icons"

// Module-level: tracks the single active audio player across all instances
let activePlayer: {
	audio: HTMLAudioElement
	stop: () => void
} | null = null

// Sets up MediaSession handlers to route keyboard media keys to the active player only
function registerMediaSession(audio: HTMLAudioElement) {
	if (!("mediaSession" in navigator)) return
	navigator.mediaSession.setActionHandler("play", () => {
		if (activePlayer?.audio === audio) {
			audio.play()
		}
	})
	navigator.mediaSession.setActionHandler("pause", () => {
		if (activePlayer?.audio === audio) {
			audio.pause()
		}
	})
}

type AudioPlayerProps = {
	src: string
}

function formatTime(seconds: number): string {
	if (!isFinite(seconds) || seconds < 0) return "00:00"
	const mins = Math.floor(seconds / 60)
	const secs = Math.floor(seconds % 60)
	return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`
}

export function AudioPlayer({ src }: AudioPlayerProps) {
	const audioRef = useRef<HTMLAudioElement>(null)
	const progressRef = useRef<HTMLDivElement>(null)

	const [isPlaying, setIsPlaying] = useState(false)
	const [progress, setProgress] = useState(0)
	const [duration, setDuration] = useState(0)
	const [currentTime, setCurrentTime] = useState(0)
	const [isLoading, setIsLoading] = useState(false)

	const stopPlayback = useCallback(() => {
		if (!audioRef.current) return
		audioRef.current.pause()
		setIsLoading(false)
	}, [])

	const makeActive = useCallback(() => {
		if (!audioRef.current) return
		activePlayer = {
			audio: audioRef.current,
			stop: stopPlayback,
		}
		registerMediaSession(audioRef.current)
	}, [stopPlayback])

	const togglePlay = () => {
		if (!audioRef.current) return

		if (isPlaying) {
			audioRef.current.pause()
		} else {
			// Stop any other playing audio first
			if (activePlayer && activePlayer.audio !== audioRef.current) {
				activePlayer.stop()
			}
			setIsLoading(true)
			makeActive()
			audioRef.current.play()
		}
	}

	const handleTimeUpdate = () => {
		const audio = audioRef.current
		if (!audio) return
		const pct = audio.duration ? (audio.currentTime / audio.duration) * 100 : 0
		setProgress(pct)
		setCurrentTime(audio.currentTime)
	}

	const handleLoadedMetadata = () => {
		const audio = audioRef.current
		if (!audio) return
		setDuration(audio.duration)
	}

	const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (!progressRef.current || !audioRef.current) return
		const rect = progressRef.current.getBoundingClientRect()
		const clickX = e.clientX - rect.left
		const pct = clickX / rect.width
		setIsLoading(true)
		audioRef.current.currentTime = pct * audioRef.current.duration
	}

	const handlePlaying = () => {
		setIsLoading(false)
	}

	const handleSeeked = () => {
		if (audioRef.current?.paused) {
			setIsLoading(false)
		}
	}

	const displayTime = isPlaying || currentTime > 0 ? currentTime : duration

	return (
		<Inline
			className={audioPlayerClassName}
			gap="2"
			backgroundColor="gray2"
			alignItems="center"
			paddingX="3"
			paddingY="2"
			borderRadius="full"
			borderWidth="1"
			borderColor="muted"
			position="relative"
		>
			{isLoading ? (
				<Box
					position="absolute"
					inset="0"
					borderRadius="full"
					display="flex"
					alignItems="center"
					justifyContent="center"
					zIndex="10"
					className={overlayClassName}
				>
					<Box className={spinnerClassName} />
				</Box>
			) : null}
			<audio
				ref={audioRef}
				src={src}
				onTimeUpdate={handleTimeUpdate}
				onLoadedMetadata={handleLoadedMetadata}
				onPlaying={handlePlaying}
				onSeeked={handleSeeked}
				onPlay={() => {
					// Block play if this is NOT the active player (e.g. browser media key fired on all audios)
					if (activePlayer && activePlayer.audio !== audioRef.current) {
						audioRef.current?.pause()
						return
					}
					setIsPlaying(true)
					setIsLoading(false)
				}}
				onPause={() => {
					setIsPlaying(false)
				}}
				onEnded={() => {
					setIsPlaying(false)
					setProgress(0)
					setCurrentTime(0)
					if (activePlayer?.audio === audioRef.current) {
						activePlayer = null
					}
				}}
				preload="metadata"
			/>
			<Button
				inline
				onClick={togglePlay}
				aria-label={isPlaying ? "Pause" : "Play"}
				color="primary_emphasis"
			>
				{isPlaying ? <Icons.Pause /> : <Icons.Play />}
			</Button>
			<Box
				flex="1"
				position="relative"
				overflow="hidden"
				cursor="pointer"
				borderRadius="md"
				ref={progressRef}
				onClick={handleProgressClick}
				className={progressBarContainerClassName}
			>
				<Box
					height="full"
					borderRadius="md"
					className={progressBarFillClassName}
					style={{
						width: `${progress}%`,
					}}
				/>
			</Box>
			<Box
				as="span"
				fontSize="sm"
				fontWeight="semibold"
				color="muted"
				whiteSpace="preserve"
				flexShrink="0"
				fontVariantNumeric="tabularNums"
				textAlign="right"
				className={timeDisplayClassName}
			>
				{formatTime(displayTime)}
			</Box>
		</Inline>
	)
}
